// src/components/Sections/CoursesPreview.jsx
import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SEO from "../SEO"; // Import your reusable SEO component

// ---- Icons ----
import { FaPaintBrush, FaChild } from "react-icons/fa";
import { GiFilmProjector } from "react-icons/gi";
import { MdAnimation, MdLightbulbOutline } from "react-icons/md";
import { SiBlender } from "react-icons/si";

import "./CoursesPreview.css";

// ---------- COURSE LIST ----------
const courseList = [
  {
    title: "Graphic Design Basics",
    subtitle: "Photoshop, Illustrator, CorelDraw",
    duration: "3 Month",
    fees: "₹800–₹1,200/hour",
    image: "/images/seo-graphicdesign.webp",
  },
  {
    title: "3D Modeling & Texturing Specialist",
    subtitle: "Maya, Blender, Substance Painter, Photoshop",
    duration: "5 Month",
    fees: "₹800–₹1,200/hour",
    image: "/images/seo-3dmodeling.webp",
  },
  {
    title: "Lighting & Render",
    subtitle: "Arnold, Blender, V-ray, Unreal engine",
    duration: "3 Months",
    fees: "₹800–₹1,200/hour",
    image: "/images/seo-lighting.webp",
  },
  {
    title: "3D Animation Fundamentals & Rigging",
    subtitle: "Maya, Blender",
    duration: "4 Months",
    fees: "₹800–₹1,200/hour",
    image: "/images/seo-animation.webp",
  },
  {
    title: "Personal 1-to-1 Custom Mentorship",
    subtitle: "Any Software",
    duration: "Flexible",
    fees: "₹800–₹1,200/hour",
    image: "/images/seo-mentorship.webp",
  },
  {
    title: "Kids & Teen Design Starter",
    subtitle: "Art, Concept & Basic 3D",
    duration: "Flexible",
    fees: "₹800–₹1,200/hour",
    image: "/images/seo-kids.webp",
  },
];

// ---------- ICON MAP ----------
const iconMap = {
  "Graphic Design Basics": <FaPaintBrush />,
  "3D Modeling & Texturing Specialist": <SiBlender />,
  "Lighting & Render": <MdLightbulbOutline />,
  "3D Animation Fundamentals & Rigging": <MdAnimation />,
  "Personal 1-to-1 Custom Mentorship": <GiFilmProjector />,
  "Kids & Teen Design Starter": <FaChild />,
};

export default function CoursesPreview() {
  const cardVariants = {
    hidden: { opacity: 0, y: 25, scale: 0.95 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { delay: i * 0.06, duration: 0.5, ease: "easeOut" },
    }),
  };

  // Use first course for default SEO preview with absolute URL and window safety
  const firstCourse = useMemo(() => courseList[0], []);
  const courseImageUrl = firstCourse?.image?.startsWith("http")
    ? firstCourse.image
    : `https://ankielabs.com${firstCourse?.image || "/images/anikielab.webp"}`;

  const currentUrl = typeof window !== "undefined" ? window.location.href : "https://ankielabs.com/courses";

  return (
    <>
      {/* 🔥 SEO Integration */}
      <SEO
        title="Skill Labs Courses — Ankielabs 3D & Design Training"
        description="Enroll in Ankielabs Skill Labs courses for Graphic Design, 3D Modeling, Animation, and Creative Mentorship. Learn from expert instructors."
        image={courseImageUrl}
        url={currentUrl}
        schemaType="Course"
        schemaData={{
          name: firstCourse?.title,
          description: "Enroll in Ankielabs Skill Labs courses for Graphic Design, 3D Modeling, Animation, and Creative Mentorship.",
          provider: {
            "@type": "Organization",
            name: "Ankielabs",
            sameAs: "https://ankielabs.com"
          },
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: "800-1200",
            priceValidUntil: "2026-12-31"
          }
        }}
      />

      <motion.section
        className="courses-section neon"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <h2 className="section-title">
          Skill <span>Labs</span>
        </h2>

        <div className="courses-grid">
          {courseList.map((course, i) => {
            const Icon = iconMap[course.title];
            return (
              <Link
                key={course.title}
                to={`/details/courses?selected=${encodeURIComponent(course.title)}`}
                className="course-link"
              >
                <motion.div
                  variants={cardVariants}
                  custom={i}
                  className="course-card"
                  whileHover={{
                    scale: 1.05,
                    boxShadow:
                      "0 10px 30px rgba(0, 203, 225, 0.25), 0 0 35px rgba(0,203,225,0.18)",
                  }}
                >
                  <div className="course-icon">{Icon}</div>
                  <h3 className="course-title">{course.title}</h3>
                  <p className="course-subtitle">{course.subtitle}</p>
                  <div className="course-meta">
                    <span>⏳ {course.duration}</span>
                    <span>💰 {course.fees}</span>
                  </div>
                </motion.div>
              </Link>
            );
          })}
        </div>
      </motion.section>
    </>
  );
}
